<template>
  <div class="grid">
    <div
      v-for="item in items"
      :key="item.id"
      class="grid__item"
      :class="{ 'grid__item--selectable': isSelectable }"
      @click="emit('itemClick', item)"
    >
      <AkParagraph
        size="xs"
        class="item__date"
      >
        {{ getFormatedDate(item.attributes.createdAt) }}
      </AkParagraph>
      <div class="ds-flex-1">
        <img
          :src="`${item.attributes.url}?h=300&w=300`"
          :alt="item.attributes.originalFilename"
          class="item__image"
        >
      </div>
      <div class="item__name">
        <AkTooltipInformation
          :content="item.attributes.originalFilename"
          placement="top"
          trigger="hover"
          type="success"
        >
          <div
            class="item-name__nugget"
            @click="copyFileNameToClipboard(item.attributes.originalFilename)"
          >
            <span class="nugget-label">{{ item.attributes.originalFilename }}</span>
            <AkIcon
              symbol="copy-keyboard"
              size="sm"
              class="nugget-ic"
            />
          </div>
        </AkTooltipInformation>
      </div>
    </div>

    <AkToaster ref="copyToast">
      {{ $t('brand.account.mediaLibrary.copyToast') }}
    </AkToaster>
  </div>
</template>

<script setup lang="ts">
import { AkParagraph, AkTooltipInformation, AkToaster } from '@ankorstore/design-system';
import { copyToClipboard } from '@core/utilities/navigator';
import { ref, toRefs } from 'vue';
import { type Media } from '../../types';
import { formatDate } from '@monolith/legacy/services/date-locale';

const copyToast = ref(null);

const props = defineProps<{
  items: Media[];
  isSelectable?: boolean;
}>();

const emit = defineEmits(['itemClick']);

const { items } = toRefs(props);

const copyFileNameToClipboard = async (filename: string): Promise<void> => {
  if (await copyToClipboard(filename)) {
    copyToast.value.openToaster();
  }
};

const getFormatedDate = (date: string): string => {
  return formatDate(date, 'P');
};
</script>

<style scoped lang="scss">
.grid {
  @apply ds-grid ds-gap-x-0.5 ds-gap-y-4 sm:ds-grid-cols-5 xs:ds-grid-cols-2 ds-grid-flow-dense;

  &__item {
    @apply ds-flex ds-flex-col ds-items-center ds-border-solid ds-border ds-border-neutral-300 ds-rounded-md ds-overflow-hidden;

    &--selectable {
      @apply ds-cursor-pointer;
    }
  }
}

.item {
  &__date {
    @apply ds-self-start ds-text-xs ds-font-bold ds-text-neutral-700 ds-p-2;
  }

  &__image {
    @apply ds-items-center;
  }

  &__name {
    height: 49px;
    @apply ds-border-t ds-border-neutral-300 ds-border-solid ds-px-1 ds-pt-1.5 ds-pb-1 ds-w-full hover:ds-bg-neutral-200 ds-cursor-pointer;
  }
}

.item-name {
  &__nugget {
    @apply ds-relative ds-h-full;
  }
}

.nugget-label {
  @apply ds-text-xs ds-font-bold ds-ml-1 ds-h-full ds-pt-0.5 ds-overflow-hidden;
  display: -webkit-inline-box;
  -webkit-line-clamp: 2; /* Show only up to 2 lines */
  -webkit-box-orient: vertical;
  word-break: break-word;
  box-sizing: border-box;
  margin-right: 20px; /* icon space */
}

.nugget-ic {
  @apply ds-absolute ds-bottom-0.5 ds-right-0.5;
}
</style>
